import React, { useCallback, useEffect, } from "react";
import "./tenantonboard.css";
import { OnboardingForms, } from "../../../typings/onboarding";
import CustomInputs from "../../common/Inputs";
import { QuestionTypeEnum } from "@propertelligent/client-api";
import { useError } from "../../../utils/context/ErrorContext";
import RadioButton from "../../common/RadioButton";
import { CustomInputTextArea } from "../../common/formControls";
import { TextCss } from "../../GlobalCss/TextCss";

const hasFollowUpText = (text: unknown) =>
  typeof text === "string" && text.trim().length > 0;

const StepFive: React.FC<any> = ({ formData, setFormData, Questionnaires }: {
  formData: any;
  setFormData: React.Dispatch<React.SetStateAction<OnboardingForms>>;
  Questionnaires: any
}) => {
  const { errors, setErrors, errorMessages, setErrorMessages, clearErrors } = useError()
  const setdata = useCallback(() => {
    if (!Questionnaires) return;

    setFormData((prevFormData) => {
      const existingQuestions = prevFormData?.Questionnaires || [];

      const updatedQuestions = Questionnaires.map(({ id, questionTypeId, choices, followUpQuestionChoiceId, followUpQuestionText }) => {
        const existingQuestion = existingQuestions.find(q => q.questionId === id);

        if (existingQuestion) {
          let updatedQuestion = { ...existingQuestion };

          if (questionTypeId === QuestionTypeEnum.SingleChoice || questionTypeId === QuestionTypeEnum.MultipleChoice) {
            delete updatedQuestion.answer;
            const selected = updatedQuestion.selectedOptionIds || [];
            const followUpSelected =
              questionTypeId === QuestionTypeEnum.SingleChoice &&
              followUpQuestionChoiceId != null &&
              selected.includes(followUpQuestionChoiceId);
            // Follow-up UI is SingleChoice-only — never seed followUpAnswer for MultipleChoice (#23)
            if (followUpSelected && hasFollowUpText(followUpQuestionText)) {
              if (updatedQuestion.followUpAnswer === undefined) {
                updatedQuestion.followUpAnswer = "";
              }
            } else {
              delete updatedQuestion.followUpAnswer;
            }
          } else if (questionTypeId === QuestionTypeEnum.Paragraph || questionTypeId === QuestionTypeEnum.ShortAnswer) {
            delete updatedQuestion.followUpAnswer;
          }

          return updatedQuestion;
        }

        const selectedOptionIds = [];

        let newQuestion: any = {
          questionId: id,
          questionTypeId,
          selectedOptionIds,
        };

        if (questionTypeId === QuestionTypeEnum.Paragraph || questionTypeId === QuestionTypeEnum.ShortAnswer) {
          newQuestion.answer = "";
        }

        return newQuestion;
      });

      return {
        ...prevFormData,
        Questionnaires: updatedQuestions
      };
    });
  }, [Questionnaires, setFormData]);

  useEffect(() => {
    setdata();
  }, [Questionnaires, setdata]);

  const handleOnClickElement = (id, optionIndex, questionTypeId, isChecked, value, followUpQuestionChoiceId, followUpQuestionText) => {
    clearErrors()
    setFormData((prevState) => {
      const updatedQuestionnaire = prevState?.Questionnaires?.map((question) => {
        if (question?.questionId === id) {
          let updatedSelectedOptions;

          if (questionTypeId === QuestionTypeEnum.SingleChoice) {
            updatedSelectedOptions = [Number(value)];
          } else if (questionTypeId === QuestionTypeEnum.MultipleChoice) {
            if (isChecked) {
              updatedSelectedOptions = [...(question?.selectedOptionIds || []), Number(value)];
            } else {
              updatedSelectedOptions = (question?.selectedOptionIds || []).filter(optId => optId !== Number(value));
            }
          }

          const updatedQuestion = {
            ...question,
            selectedOptionIds: updatedSelectedOptions,
          };

          const followUpSelected =
            questionTypeId === QuestionTypeEnum.SingleChoice &&
            hasFollowUpText(followUpQuestionText) &&
            ((Number(value) === followUpQuestionChoiceId) ||
              (updatedSelectedOptions || []).includes(followUpQuestionChoiceId));

          if (followUpSelected) {
            updatedQuestion.followUpAnswer = question.followUpAnswer || "";
            delete updatedQuestion.answer;
          } else {
            delete updatedQuestion.followUpAnswer;
            delete updatedQuestion.answer;
          }

          return updatedQuestion;
        }
        return question;
      });

      return {
        ...prevState,
        Questionnaires: updatedQuestionnaire,
      };
    });
  };


  const handleChangeFollowup = useCallback((questionIndex, event) => {
    const { value, name } = event.target;

    setFormData((prevState) => {
      const updatedQuestionnaire = prevState?.Questionnaires?.map((question, index) => {
        if (index === questionIndex) {
          return { ...question, [name]: value, };
        }
        return question;
      });
      return { ...prevState, Questionnaires: updatedQuestionnaire, };
    });
    const errorKey = `${name}[${questionIndex}]`;
    setErrors((prevErrors) => ({ ...prevErrors, [errorKey]: false }));
    setErrorMessages((prevMessages) => ({ ...prevMessages, [errorKey]: "" }));
  }, [setFormData, setErrors, setErrorMessages]);

  return (
    <>
      <div className="question-list">
        {Array.isArray(Questionnaires) &&
          Questionnaires?.map((item, index) => {
            const selectedIds = formData?.[index]?.selectedOptionIds || [];
            const showFollowUp =
              item?.questionTypeId === QuestionTypeEnum.SingleChoice &&
              hasFollowUpText(item?.followUpQuestionText) &&
              selectedIds.includes(item?.followUpQuestionChoiceId);

            return (
              <div key={index} className="questionaire_question-row">
                <p className={TextCss.black.sub_heading_600_14}>{item?.questionText}</p>
                {(item?.questionTypeId === QuestionTypeEnum.SingleChoice || item?.questionTypeId === QuestionTypeEnum.MultipleChoice) && (
                  <div className="question-option ">
                    {item?.choices?.map((option, optionIndex) => (
                      <RadioButton
                        key={optionIndex}
                        type={item?.questionTypeId === QuestionTypeEnum.SingleChoice ? "radio" : "checkbox"}
                        id={`info-${item?.questionTypeId === QuestionTypeEnum.SingleChoice ? 'radio' : 'checkbox'}-${index}-${optionIndex}`}
                        label={option?.optionText}
                        value={Number(option?.id)}
                        name={`question-${index}`}
                        checked={selectedIds.includes(option.id)}
                        onChange={(e) => handleOnClickElement(item?.id, optionIndex, item?.questionTypeId, e.target.checked, e.target.value, item?.followUpQuestionChoiceId, item?.followUpQuestionText)}
                      />
                    ))}
                    {errors[`selectedOptionIds[${index}]`] && (
                      <p className="errorMessageStyles">{errorMessages[`selectedOptionIds[${index}]`] || "Please select an option"}</p>
                    )}
                  </div>)}
                {showFollowUp && (
                    <div className="w-50">
                      <CustomInputs
                        labelText={item?.followUpQuestionText}
                        name="followUpAnswer"
                        type="text"
                        placeholder="Additional Details"
                        value={formData?.[index]?.followUpAnswer ? formData?.[index]?.followUpAnswer : ""}
                        onChange={(e) => handleChangeFollowup(index, e)}
                        isError={errors[`followUpAnswer[${index}]`]}
                        errorMessages="Answer is required"
                        isFormControl={true}
                        myClassName="followUpAnswer"
                        maxLength={50}
                        width="260px"
                      />
                    </div>
                  )}

                {item?.questionTypeId === QuestionTypeEnum.Paragraph && (
                  <div className="w-50">
                    <CustomInputTextArea
                      name="answer"
                      placeholder="Enter a detailed response"
                      required
                      value={formData?.[index]?.answer === "provide answer" ? "" : formData?.[index]?.answer || ""}
                      onChange={(e) => { handleChangeFollowup(index, e) }}
                      
                      isError={errors[`answer[${index}]`]}
                      errorMessages="Answer is required "
                      myClassName="normal_text black questionnaire-answer-text"
                      rows={4}
                      width="100%"
                    />
                  </div>
                )}
                {item?.questionTypeId === QuestionTypeEnum.ShortAnswer && (
                  <div className="w-50">
                    <CustomInputTextArea
                      name="answer"
                      placeholder="Enter a brief response"
                      required
                      isFormControl
                      value={formData?.[index]?.answer === "provide answer" ? "" : formData?.[index]?.answer || ""}
                      onChange={(e) => { handleChangeFollowup(index, e) }}
                      isError={errors[`answer[${index}]`]}
                      errorMessages="Short Answer required "
                      myClassName="normal_text black questionnaire-answer-text"
                      rows={5}
                      width="100%"
                    />
                  </div>
                )}
              </div>
            )
          }
          )}
      </div>
    </>
  );
};

export default StepFive;
